// ============================================
// Grading Types
// ============================================

import type { ScoringConfig, ContentConfig } from './config';

/**
 * Optional context passed from ContentPlayer when invoking a grader (e.g. essay API needs contentId).
 */
export interface GradingContext {
    contentId: string;
    config?: ContentConfig;
    /** Learner id for AI grading APIs (e.g. speaking multipart `user_id`). */
    userId?: string;
    /**
     * Session JWT for write endpoints that require `SessionTokenAuthentication`
     * (e.g. essay/speaking grade-job in multi-player mode).
     * When provided, the grader uses this token instead of the global API client auth
     * so the view-token configured for content reads is not overwritten.
     */
    sessionToken?: string;
    /**
     * EAA-2117: host tenant id (ContentPlayer `tenantId` prop). Graders send it on the
     * unsaved-draft grade-job and on SpeechAce scoring, where there is no saved content to
     * resolve the tenant from and a staff/superadmin JWT carries none.
     */
    tenantId?: string;
    /**
     * ELMS-2377: host-driven FORCE_SUBMIT only. An AI grader that runs as a backend grade-job
     * (essay, speaking criteria) may stop once the job exists on the server instead of polling
     * it, and return `{ pending: true }`. The backend writes the score onto the content session
     * when the job completes (ELMS-2365 write-back), so the LMS no longer waits for the AI.
     * Honoured only with a `sessionToken` (the job must carry the session id) and a saved
     * content id; otherwise the grader keeps the synchronous behaviour.
     */
    deferAiResult?: boolean;
}

const PERSISTED_CONTENT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * EAA-2117: true when `contentId` is a saved content UUID. An unsaved draft (question-bank
 * `/new` preview) carries a client-generated id the backend has never seen, so a grader must
 * use the draft grade path instead of `/content/<id>/...`.
 */
export function isPersistedContentId(contentId: string | undefined | null): boolean {
    return Boolean(contentId && PERSISTED_CONTENT_ID.test(contentId));
}

/**
 * Grader for evaluating content answers
 */
export interface ContentGrader<TData = unknown, TAnswer = unknown> {
    /** Grade an answer against content data */
    grade: (
        content: TData,
        answer: TAnswer,
        config?: ScoringConfig,
        context?: GradingContext
    ) => GradingResult | Promise<GradingResult>;
    /**
     * True when `grade()` would issue a network/AI request for this content (essay/speaking
     * grade-job, SpeechAce...). Review mode (`isReview`) must not re-grade such content when no
     * stored result exists: the request needs write permission a viewer token lacks (403) and
     * would start a billable AI job on every open. Omitted = synchronous/local grader.
     */
    requiresNetwork?: (content: TData, config?: ScoringConfig) => boolean;
    /** Get the correct answer for content */
    getCorrectAnswer?: (content: TData) => TAnswer;
    /**
     * Validate that an answer is complete/valid for submit.
     * `opts.allowPartial` is set on a host-forced submit (FORCE_SUBMIT / `deferAiGrading`): graders
     * of multi-sub-question groups (mcq-set, quiz) then accept an answer with >=1 answered
     * sub-question and grade the blanks as wrong, instead of dropping the whole group's score.
     */
    validateAnswer?: (answer: TAnswer, content: TData, opts?: { allowPartial?: boolean }) => boolean;
}

/**
 * Result of grading an answer
 */
export interface GradingResult {
    /** Whether the answer is correct */
    isCorrect: boolean;
    /** Score achieved */
    score: number;
    /** Maximum possible score */
    max: number;
    /** Feedback message */
    feedback?: string;
    /** The correct answer */
    correctAnswer?: unknown;
    /** Additional grading details */
    details?: Record<string, unknown>;
    /**
     * ELMS-2377: the AI grade-job(s) were created on the server but NOT awaited (see
     * {@link GradingContext.deferAiResult}). `score`/`max` are placeholders and MUST NOT be
     * persisted or shown; the backend stamps the real score on the session later.
     */
    pending?: boolean;
}

/** ELMS-2377: options of the programmatic (FORCE_SUBMIT) submit. */
export interface ImperativeSubmitOptions {
    /** Create AI grade-jobs but do not wait for their results (host FORCE_SUBMIT only). */
    deferAiGrading?: boolean;
}

/**
 * Programmatic submit exposed through `ContentPlayer.imperativeSubmitRef`.
 * Resolves `true` after grading + onSubmit settle, `'deferred'` when AI grading was only
 * started (no onSubmit, no score yet), `false` when skipped/failed.
 */
export type ImperativeSubmit = (opts?: ImperativeSubmitOptions) => Promise<boolean | 'deferred'>;

// ============================================
// Question Counter
// ============================================

/**
 * Counts the number of gradable answer units in content data.
 *
 * This is used by metadata computation and player UIs to display
 * progress indicators (e.g. "3 / 5 answered").
 *
 * What constitutes a "question":
 *  - MCQ → 1
 *  - Match → number of match pairs (items)
 *  - FITB → number of blanks
 *  - Ordering → number of ordering items
 *  - Essay → 1
 */
export interface QuestionCounter<TData = unknown> {
    /** Count the total number of gradable answer units in the data. */
    countQuestions: (data: TData) => number;
}

// ============================================
// Validation Types
// ============================================

/**
 * Validator for content data
 */
export interface ContentValidator<TData = unknown> {
    /** Validate content data */
    validate: (data: TData) => ValidationResult;
}

/**
 * Result of validation
 */
export interface ValidationResult {
    /** Whether the data is valid */
    isValid: boolean;
    /** Validation errors */
    errors: ValidationError[];
}

/**
 * A single validation error
 */
export interface ValidationError {
    /** Field path that has the error */
    field: string;
    /** Error message */
    message: string;
    /** Error code for programmatic handling */
    code?: string;
}

/** ELMS-2369: marker carried by errors a learner may see (friendly text is chosen by the player, i18n). */
export const GRADING_UNAVAILABLE_CODE = 'GRADING_UNAVAILABLE';

export class GradingUnavailableError extends Error {
    readonly code = GRADING_UNAVAILABLE_CODE;
    // The raw cause is intentionally NOT kept: it was already logged sanitised, and holding it
    // would let console/telemetry serialisers print URLs/tokens again.
    constructor(_cause?: unknown) {
        super('Grading unavailable');
        this.name = 'GradingUnavailableError';
    }
}

/** True for a `GradingUnavailableError` (also across duplicated bundles, via `code`). */
export function isGradingUnavailableError(err: unknown): boolean {
    return (
        typeof err === 'object' &&
        err !== null &&
        (err as { code?: unknown }).code === GRADING_UNAVAILABLE_CODE
    );
}

/**
 * Log a sanitised technical reason (never URLs / tokens / headers) for console + telemetry.
 * Only a short message is kept, with URLs and bearer/JWT-looking tokens masked.
 */
export function describeGradingFailure(err: unknown): string {
    const raw =
        err instanceof Error
            ? err.message
            : err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string'
              ? (err as { message: string }).message
              : String(err ?? '');
    const status = (err as { status?: unknown } | null)?.status;
    return (
        raw
            .replace(/https?:\/\/\S+/gi, '[url]')
            .replace(/\b(?:Bearer|Session)\s+[\w.~+/=-]+/gi, '$1 [token]')
            .replace(/eyJ[\w-]+\.[\w-]+\.[\w-]+/g, '[token]')
            .replace(/([?&](?:token|key|access_token|sig)=)[^&\s]+/gi, '$1[token]')
            .slice(0, 300) + (typeof status === 'number' ? ` (status ${status})` : '')
    );
}

/** Run a grader body; any failure becomes a `GradingUnavailableError` (learner-safe) after logging. */
export async function runGradingSafely<T>(tag: string, body: () => Promise<T>): Promise<T> {
    try {
        return await body();
    } catch (err) {
        if (isGradingUnavailableError(err)) throw err;
        console.error(`[${tag}] grading failed: ${describeGradingFailure(err)}`);
        throw new GradingUnavailableError(err);
    }
}
